import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import React, { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import {
  SORT_DEPARTURE_SOON,
  SORT_NEWEST,
  SORT_OPTION_PRICE_ASC,
  SORT_OPTION_PRICE_DESC,
  SORT_OPTION_RECOMMENDED,
} from 'constants/offerListFilters'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

export const CruiseSearchSortOptions = [
  SORT_OPTION_RECOMMENDED,
  SORT_OPTION_PRICE_ASC,
  SORT_OPTION_PRICE_DESC,
  SORT_DEPARTURE_SOON,
  SORT_NEWEST,
]

interface Props {
  sort: string
  onChange: (newSort: App.OfferListSortByOptions) => void
}

function CruiseSortDropdown(props: Props) {
  const { onChange, sort } = props
  const intl = useIntl()

  const [open, toggleSortOpen, , closeSort] = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const sortLabels: Record<string, string> = useMemo(() => ({
    [SORT_OPTION_RECOMMENDED.value]: intl.formatMessage({
      id: 'NFSabg4dtB',
      defaultMessage: 'Recommended',
      description: 'Sort option label for recommended cruise results',
    }),
    [SORT_OPTION_PRICE_ASC.value]: intl.formatMessage({
      id: 'nh6FgfDg51',
      defaultMessage: 'Price (Lowest)',
      description: 'Sort option label for lowest price first cruise results',
    }),
    [SORT_OPTION_PRICE_DESC.value]: intl.formatMessage({
      id: 'FtgB693oBR',
      defaultMessage: 'Price (Highest)',
      description: 'Sort option label for highest price first cruise results',
    }),
    [SORT_DEPARTURE_SOON.value]: intl.formatMessage({
      id: '7TE9LGCNQI',
      defaultMessage: 'Depart soon',
      description: 'Sort option label for soonest departure cruise results',
    }),
    [SORT_NEWEST.value]: intl.formatMessage({
      id: 'A50BwHgKiq',
      defaultMessage: 'Newest',
      description: 'Sort option label for newest cruise results',
    }),
  }), [intl])

  const onSortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.value as App.OfferListSortByOptions)
    closeSort()
  }, [closeSort, onChange])

  const selectedLabel = useMemo(() => {
    const foundOption = CruiseSearchSortOptions.find(option => option.value === sort)
    if (foundOption) return sortLabels[foundOption.value] ?? foundOption.label
    return sortLabels[SORT_OPTION_RECOMMENDED.value]
  }, [sort, sortLabels])

  const sortPrefix = intl.formatMessage({
    id: 'zaccWrBMR3',
    defaultMessage: 'Sort: {selectedLabel}',
    description: 'Label for cruise sort dropdown chip showing selected sort option',
  }, { selectedLabel })

  return <>
    <AssistChip
      variant="filled"
      size="medium"
      onClick={toggleSortOpen}
      endIcon={<DropdownChevron open={open} />}
      ref={triggerRef}
    >
      {sortPrefix}
    </AssistChip>
    <DropdownList
      size="S"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      open={open}
      onClose={closeSort}
    >
      <RadioGroup direction="vertical" gap={16}>
        {CruiseSearchSortOptions.map(option => <RadioInput
          value={option.value}
          onChange={onSortChange}
          key={option.value}
          checked={option.value ? option.value === sort : !sort}
          name="sort"
        >
          {sortLabels[option.value] ?? option.label}
        </RadioInput>)}
      </RadioGroup>
    </DropdownList>
  </>
}

export default React.memo(CruiseSortDropdown)
